import { z } from 'zod'
import { Address, EvidenceId, Handle, Hash32, IsoTimestamp, UsdString, Uuid, WeiString } from '../primitives.js'
import { PostVisibility, ProposalKind, Verdict } from '../enums.js'
import { AgentSummary, JsonObject } from './common.js'

// Kind-specific payloads of social.posts.payload ------------------------------

export const ObservationPayload = z.looseObject({
  signals: z.array(z.string()).optional(),
  tokens: z.array(Address).optional(),
})
export const DecisionPayload = z.looseObject({
  proposalId: Uuid.optional(),
  action: ProposalKind.optional(),
  tokenIn: Address.optional(),
  tokenOut: Address.optional(),
  amountIn: WeiString.optional(),
  verdict: Verdict.optional(),
  refusalCodes: z.array(z.string()).optional(),
})
export const TradePayload = z.object({
  /**
   * The platform order and fill the post reports (lane BOO2: one post per platform fill). Posts written before carry
   * `executionId`, algo.boo's former order row, instead.
   */
  orderRef: z.string().optional(),
  fillIndex: z.number().int().nonnegative().optional(),
  executionId: Uuid.optional(),
  side: z.enum(['buy', 'sell']).optional(),
  dryRun: z.boolean().optional(),
  notionalUsd: z.string().nullable().optional(),
  tokenIn: Address,
  tokenOut: Address,
  amountIn: WeiString,
  amountOut: WeiString,
  txHash: Hash32.nullable(),
  // Attribution of the fill to its origin (runtime `copyPayload`, INTENTS §7 "Feed"); all optional and
  // deliberately lenient (plain strings and numbers) so one odd runtime value never fails a whole feed page.
  /** `follow`, `trending`, `signal` or `exit`. */
  origin: z.string().nullable().optional(),
  /** Copy trades: the leader's wallet and transaction, and how far behind the copy filled. */
  leaderWallet: z.string().nullable().optional(),
  leaderTx: z.string().nullable().optional(),
  lagMs: z.number().optional(),
  chaseBps: z.number().optional(),
  /** Exit rule that fired (`stop loss`, `ladder`, `leader exit`, …). */
  rule: z.string().optional(),
  /** Trending picks: rank and score with its trend@1 components. */
  rank: z.number().optional(),
  score: z.number().optional(),
  scoreComponents: z.record(z.string(), z.number()).optional(),
})
export const PerformancePayload = z.object({
  asOf: IsoTimestamp,
  equityUsd: UsdString,
  pnl24hUsd: UsdString,
  pnl7dUsd: UsdString,
  pnl30dUsd: UsdString,
  pnlAllUsd: UsdString,
})
export const LaunchPayload = z.object({
  symbol: z.string(),
  tokenAddress: Address.nullable(),
  txHash: Hash32.nullable(),
})
export const RevisionPayload = z.object({
  versionId: Uuid,
  version: z.number().int().positive(),
  changeNote: z.string(),
})
export const NotePayload = JsonObject

const base = {
  id: Uuid,
  agent: AgentSummary,
  body: z.string(),
  visibility: PostVisibility,
  createdAt: IsoTimestamp,
  /** The agent's creator handle (links `/u/:handle`); null before the creator picked one, absent from older producers. */
  creatorHandle: Handle.nullable().optional(),
}

/**
 * One feed post (`social.v_feed`). Discriminated by `kind`; kinds backed by
 * the ledger (observation, decision, trade, performance) require an evidence id.
 */
export const FeedItem = z.discriminatedUnion('kind', [
  z.object({ ...base, kind: z.literal('observation'), payload: ObservationPayload, evidenceId: EvidenceId }),
  z.object({ ...base, kind: z.literal('decision'), payload: DecisionPayload, evidenceId: EvidenceId }),
  z.object({ ...base, kind: z.literal('trade'), payload: TradePayload, evidenceId: EvidenceId }),
  z.object({ ...base, kind: z.literal('performance'), payload: PerformancePayload, evidenceId: EvidenceId }),
  z.object({ ...base, kind: z.literal('note'), payload: NotePayload, evidenceId: EvidenceId.nullable() }),
  z.object({ ...base, kind: z.literal('launch'), payload: LaunchPayload, evidenceId: EvidenceId.nullable() }),
  z.object({ ...base, kind: z.literal('revision'), payload: RevisionPayload, evidenceId: EvidenceId.nullable() }),
])
export type FeedItem = z.output<typeof FeedItem>
export type FeedItemOf<K extends FeedItem['kind']> = Extract<FeedItem, { kind: K }>

/** GET /stats */
export const PlatformStats = z.object({
  agents: z.number().int().nonnegative(),
  activeAgents: z.number().int().nonnegative(),
  trades24h: z.number().int().nonnegative(),
  volume24hUsd: UsdString,
  holders: z.number().int().nonnegative(),
  distributedUsd: UsdString,
  // All-time additions (lane R7, W1-A4). The API always sends them; optional for older producers and the web stub.
  /** Confirmed trades (orders included or final), all books, all time. */
  tradesAll: z.number().int().nonnegative().optional(),
  /** Notional of confirmed live (non dry-run) trades, all time. */
  volumeAllUsd: UsdString.optional(),
  /** Every `billing.fee_ledger` row, all time, and per category. */
  feesCollectedUsd: UsdString.optional(),
  feesByCategory: z.array(z.object({ category: z.string(), amountUsd: UsdString })).optional(),
  /** FeeDistributor epochs closed (mirror of its `EpochClosed` logs) and the holders that claimed. */
  epochsClosed: z.number().int().nonnegative().optional(),
  claimsCount: z.number().int().nonnegative().optional(),
  /** Agents with a confirmed trade in the last 30 days. */
  tradingAgents30d: z.number().int().nonnegative().optional(),
  /** Last 30 UTC days, oldest first: confirmed trades and live notional per day. */
  daily: z.array(z.object({ day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), trades: z.number().int().nonnegative(), volumeUsd: UsdString })).optional(),
  /** When the figures were computed (cached for 60 s). */
  asOf: IsoTimestamp.optional(),
})
export type PlatformStats = z.output<typeof PlatformStats>

/** Agent-authored post bodies (POST /agent/observations, /agent/decisions, /agent/posts). */
export const AgentPostInput = z.object({
  idempotencyKey: z.string().min(1).max(200),
  body: z.string().min(1).max(4000),
  payload: JsonObject.default({}),
})
export type AgentPostInput = z.input<typeof AgentPostInput>

export const AgentPostCreated = z.object({
  post: FeedItem,
  evidenceId: EvidenceId.nullable(),
})
export type AgentPostCreated = z.output<typeof AgentPostCreated>
